import { useCallback, useEffect, useState } from "react";
import { Button, Card, Cluster, FormField, Input, Select, Stack, useToast } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import { loxleyApi, type ConnectorKey, type CreatedConnectorKey, type LoxleyStatus } from "../lib/loxleyApi";
import { useLocale } from "../shell/LocaleProvider";

function when(value: string | null, format: (iso: string) => string): string {
  return value ? format(value) : "Never";
}

/**
 * User settings: connect Claude, ChatGPT or another MCP client to Saleslox as yourself.
 * Only you can make a key for you; your org admin can see and revoke it.
 */
export function LoxleyConnectionsCard({ orgId }: { orgId: string }) {
  const toast = useToast();
  const { formatDateTime } = useLocale();
  const [status, setStatus] = useState<LoxleyStatus | null>(null);
  const [keys, setKeys] = useState<ConnectorKey[]>([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"read" | "read_write">("read_write");
  const [lifetime, setLifetime] = useState(90);
  const [created, setCreated] = useState<CreatedConnectorKey | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    loxleyApi.status(orgId).then(setStatus).catch(() => setStatus(null));
    loxleyApi.myKeys(orgId).then(setKeys).catch(() => setKeys([]));
  }, [orgId]);

  useEffect(load, [load]);

  if (!status?.enabled || !status.has_seat) return null;

  async function create() {
    setSaving(true);
    try {
      const key = await loxleyApi.createKey(orgId, { name: name.trim(), scope, lifetime_days: lifetime });
      setCreated(key);
      setName("");
      load();
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : "The key could not be created.", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function revoke(key: ConnectorKey) {
    try {
      await loxleyApi.revokeKey(orgId, key.id);
      toast.show(`${key.name} can no longer reach Saleslox.`, "success");
      load();
    } catch (error) {
      toast.show(error instanceof ApiError ? error.message : "The key could not be revoked.", "danger");
    }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.show("Copied.", "success");
    } catch {
      toast.show("Copy did not work. Select the text and copy it.", "danger");
    }
  }

  const claudeCode = `claude mcp add --transport http saleslox ${status.mcp_url} --header "Authorization: Bearer ${created?.secret ?? "<your key>"}"`;

  return (
    <Card>
      <Stack gap={4}>
        <Stack gap={2}>
          <h2 className="home-section-title">Connected AI apps</h2>
          <p className="home-subtitle">
            Let Claude, ChatGPT or another AI app work in Saleslox as you, with exactly your permissions.
            Every change it asks for waits for your confirmation; deletes, money and anything sent to a
            customer can only be approved here in Saleslox.
          </p>
        </Stack>
        {!status.mcp_available ? (
          <p role="status">
            External AI apps are switched off for your organization right now. Ask your organization admin.
          </p>
        ) : (
          <>
            <Stack gap={2}>
              <strong>Saleslox MCP server address</strong>
              <Cluster gap={2} align="center">
                <code className="loxley-secret">{status.mcp_url}</code>
                <Button size="sm" variant="secondary" onClick={() => void copy(status.mcp_url)}>Copy</Button>
              </Cluster>
              <p className="home-subtitle">
                Claude (web, desktop and mobile) and ChatGPT: add a custom connector with this address and sign in
                with your Saleslox account when asked. No key is needed for those.
              </p>
            </Stack>

            <Stack gap={3}>
              <strong>Personal keys (Claude Code, the Claude or OpenAI API, other MCP tools)</strong>
              <Cluster gap={3} align="end">
                <FormField
                  label="Name"
                  tooltip="A name you will recognise later, such as Claude Code on my laptop. The customer and the AI app do not see this name."
                >
                  <Input value={name} maxLength={80} placeholder="Claude Code on my laptop"
                    onChange={(event) => setName(event.target.value)} />
                </FormField>
                <FormField
                  label="Access"
                  tooltip="Read only lets the app look up records and cannot propose changes. Read, and propose changes lets it prepare updates. You still confirm each change in Saleslox. Deletes, money, and anything sent to a customer can only be approved in Saleslox."
                >
                  <Select value={scope} onChange={(event) => setScope(event.target.value as "read" | "read_write")}>
                    <option value="read_write">Read, and propose changes</option>
                    <option value="read">Read only</option>
                  </Select>
                </FormField>
                <FormField
                  label="Expires after"
                  tooltip="The key stops working after this many days. You can revoke it sooner from the list below."
                >
                  <Select value={String(lifetime)} onChange={(event) => setLifetime(Number(event.target.value))}>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">1 year</option>
                  </Select>
                </FormField>
                <Button onClick={() => void create()} disabled={saving || !name.trim()}>Create key</Button>
              </Cluster>
              {created ? (
                <Stack gap={2} role="status">
                  <strong>Copy your key now. It is not shown again.</strong>
                  <Cluster gap={2} align="center">
                    <code className="loxley-secret">{created.secret}</code>
                    <Button size="sm" onClick={() => void copy(created.secret)}>Copy key</Button>
                  </Cluster>
                  <p className="home-subtitle">Claude Code:</p>
                  <pre className="loxley-snippet">{claudeCode}</pre>
                  <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>I have saved it</Button>
                </Stack>
              ) : null}
            </Stack>
          </>
        )}

        {keys.length ? (
          <table className="loxley-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Access</th><th>Last used</th><th>Expires</th><th /></tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id}>
                  <td>{key.name}</td>
                  <td>{key.kind === "api_key" ? `Key ${key.prefix}…` : "Signed-in app"}</td>
                  <td>{key.scope === "read" ? "Read only" : "Read and propose"}</td>
                  <td>{when(key.last_used_at, formatDateTime)}</td>
                  <td>{when(key.expires_at, formatDateTime)}</td>
                  <td><Button size="sm" variant="danger" onClick={() => void revoke(key)}>Revoke</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Stack>
    </Card>
  );
}
